import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BoltIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  MegaphoneIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import Confetti from 'react-confetti';
import AdminLayout from '../components/AdminLayout';
import useApi from '../hooks/useApi';

const ILS_TO_USD_RATE = 3.5;
const ENTITLED_STATUSES = new Set(['active', 'past_due']);

const PRODUCT_META = [
  { key: 'site', label: 'Website', icon: GlobeAltIcon, color: 'bg-sky-500' },
  { key: 'crm', label: 'CRM', icon: UserGroupIcon, color: 'bg-violet-500' },
  { key: 'newsletter', label: 'Newsletter', icon: MegaphoneIcon, color: 'bg-amber-500' },
  { key: 'omni', label: 'Omni Agent', icon: SparklesIcon, color: 'bg-emerald-500' },
];

const HEALTH_STYLES = {
  healthy: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  onboarding: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  attention: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  risk: 'bg-rose-50 text-rose-700 ring-rose-600/20',
  inactive: 'bg-slate-100 text-slate-600 ring-slate-500/20',
};

const formatMoney = (value, { compact = false } = {}) => {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: compact ? 0 : 2,
    notation: compact && Math.abs(amount) >= 10000 ? 'compact' : 'standard',
  }).format(amount);
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const parseComponents = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const annualizedUsd = (bundle) => {
  const amount = Number(bundle?.amount) || 0;
  const usdAmount = String(bundle?.currency || '').toUpperCase() === 'ILS'
    ? amount / ILS_TO_USD_RATE
    : amount;
  return bundle?.billing_period === 'monthly' ? usdAmount * 12 : usdAmount;
};

const creditState = (bundle) => {
  const included = Number(bundle?.included_credits) || 0;
  const used = Number(bundle?.used_credits) || 0;
  const topup = Number(bundle?.topup_credits) || 0;
  const available = Math.max(0, included - used) + topup;
  const total = included + topup;
  const remainingPct = total > 0 ? Math.round((available / total) * 100) : null;
  return { included, used, topup, available, total, remainingPct };
};

const includesSiteComponent = (bundle) => {
  const components = parseComponents(bundle?.components);
  // Server normalizeComponents treats an empty list as every product.
  if (components.length === 0) return true;
  return components.includes('site');
};

const isPaymentIncomplete = (bundle) => {
  if (bundle?.status === 'pending_payment') return true;
  // Manual grant with no iCount/PayPal reference and no out-of-band mark —
  // sales still needs to record payment. Do not hide this behind "start onboarding".
  if (!ENTITLED_STATUSES.has(bundle?.status)) return false;
  // external_paid_at is the out-of-band mark. It is not a provider reference,
  // so a later recurring payment link can still be minted for renewal.
  return bundle?.activation_method === 'manual'
    && !bundle?.payment_reference
    && !bundle?.external_paid_at;
};

const getHealth = (bundle) => {
  const credits = creditState(bundle);
  const websites = Number(bundle?.website_count) || 0;
  const liveWebsites = Number(bundle?.deployed_website_count) || 0;

  if (bundle?.status === 'past_due' || bundle?.cancel_at_period_end) {
    return { key: 'risk', label: bundle?.status === 'past_due' ? 'At risk' : 'Cancelling' };
  }
  if (isPaymentIncomplete(bundle)) {
    return { key: 'attention', label: 'Awaiting payment' };
  }
  if (!ENTITLED_STATUSES.has(bundle?.status)) {
    return { key: 'inactive', label: bundle?.status || 'Inactive' };
  }
  if (includesSiteComponent(bundle)) {
    if (websites === 0) return { key: 'onboarding', label: 'Needs site' };
    if (liveWebsites === 0) return { key: 'onboarding', label: 'Needs publish' };
  }
  if (credits.remainingPct !== null && credits.remainingPct <= 20) {
    return { key: 'attention', label: 'Credits low' };
  }
  return { key: 'healthy', label: 'Healthy' };
};

const getAttention = (bundle) => {
  const health = getHealth(bundle);
  const map = {
    'At risk': {
      priority: 0,
      title: 'Payment is past due',
      detail: 'Restore billing before access lapses.',
      action: 'Resolve billing',
    },
    Cancelling: {
      priority: 1,
      title: 'Cancellation is scheduled',
      detail: `Bundle ends ${formatDate(bundle?.period_end)}.`,
      action: 'Review account',
    },
    'Awaiting payment': {
      priority: 2,
      title: 'Payment is not complete',
      detail: 'Follow up on the open bundle payment.',
      action: 'Complete payment',
    },
    'Needs site': {
      priority: 3,
      title: 'Customer has not started',
      detail: 'The bundle is active but no site exists.',
      action: 'Start onboarding',
    },
    'Needs publish': {
      priority: 4,
      title: 'Site is not live',
      detail: 'A site exists but has not been published.',
      action: 'Open account',
    },
    'Credits low': {
      priority: 5,
      title: 'Credits are running low',
      detail: `${creditState(bundle).remainingPct}% of credits remain.`,
      action: 'Review usage',
    },
  };
  const item = map[health.label];
  return item ? { ...item, bundle, health } : null;
};

const formatDuration = (seconds) => {
  const value = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(value / 60);
  const secs = value % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const MetricCard = ({ label, value, detail, icon: Icon, tone = 'slate', loading }) => {
  const toneClasses = {
    slate: 'bg-white text-slate-950 border-slate-200',
    blue: 'bg-white text-slate-950 border-slate-200',
    green: 'bg-white text-slate-950 border-slate-200',
    rose: 'bg-white text-slate-950 border-rose-200',
  };
  const iconClasses = {
    slate: 'bg-sky-50 text-sky-700',
    blue: 'bg-sky-50 text-sky-700',
    green: 'bg-emerald-50 text-emerald-700',
    rose: 'bg-rose-50 text-rose-700',
  };

  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${toneClasses[tone]}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
            {label}
          </p>
          {loading ? (
            <div className="mt-3 h-9 w-28 animate-pulse rounded bg-slate-100" />
          ) : (
            <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
          )}
          <p className="mt-2 text-sm text-slate-500">{detail}</p>
        </div>
        <div className={`rounded-xl p-2.5 ${iconClasses[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

const GeneratingWebsitesWidget = ({ refreshKey }) => {
  const { data } = useApi('/api/admin/websites/generating', [refreshKey]);
  const [elapsedTimes, setElapsedTimes] = useState({});
  const websites = useMemo(() => data?.data?.websites || [], [data?.data?.websites]);

  useEffect(() => {
    if (websites.length === 0) return undefined;
    const updateTimes = () => {
      const now = Date.now();
      setElapsedTimes(Object.fromEntries(
        websites.map((website) => [
          website.id,
          Math.floor((now - new Date(website.created_at).getTime()) / 1000),
        ])
      ));
    };
    updateTimes();
    const interval = setInterval(updateTimes, 1000);
    return () => clearInterval(interval);
  }, [websites]);

  if (websites.length === 0) return null;

  return (
    <section className="mt-6 rounded-2xl border border-sky-200 bg-sky-50/70 p-5">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-sky-100 p-2 text-sky-700">
          <SparklesIcon className="h-5 w-5 animate-pulse" />
        </div>
        <div>
          <h2 className="font-semibold text-slate-900">Platform activity</h2>
          <p className="text-sm text-slate-600">{websites.length} website{websites.length === 1 ? '' : 's'} generating now</p>
        </div>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {websites.map((website) => {
          const elapsed = elapsedTimes[website.id] || Math.floor(website.total_generation_seconds || 0);
          const isStuck = elapsed > 720;
          return (
            <Link
              key={website.id}
              to={`/websites/${website.id}`}
              className={`rounded-xl border bg-white p-4 transition hover:-translate-y-0.5 hover:shadow-sm ${isStuck ? 'border-rose-200' : 'border-sky-100'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="truncate font-medium text-slate-900">{website.name || 'Unnamed website'}</p>
                <span className={`font-mono text-xs ${isStuck ? 'text-rose-600' : 'text-slate-500'}`}>
                  {formatDuration(elapsed)}
                </span>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${isStuck ? 'bg-rose-500' : 'bg-sky-500'}`}
                  style={{ width: `${website.progress?.progress || 0}%` }}
                />
              </div>
              <p className="mt-2 truncate text-xs text-slate-500">{website.progress?.message || 'Starting generation'}</p>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const Dashboard = () => {
  const [autoRefresh, setAutoRefresh] = useState('off');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const previousBundlesRef = useRef(null);

  const { data: analytics, loading: analyticsLoading } = useApi('/api/admin/analytics/overview', [refreshKey]);
  const {
    data: bundleResponse,
    loading: bundlesLoading,
    error: bundlesError,
  } = useApi('/api/admin/bundles?limit=200', [refreshKey]);

  const bundles = useMemo(() => bundleResponse?.bundles || [], [bundleResponse?.bundles]);
  const entitledBundles = useMemo(
    () => bundles.filter((bundle) => ENTITLED_STATUSES.has(bundle.status)),
    [bundles]
  );
  const attentionItems = useMemo(
    () => bundles
      .map(getAttention)
      .filter(Boolean)
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 6),
    [bundles]
  );

  const annualRecurringRevenue = Number(analytics?.data?.subscriptions?.annualRecurringRevenue) || 0;
  const activeBundles = analytics?.data?.subscriptions?.activeEngagementBundles ?? entitledBundles.length;
  const revenueAtRisk = bundles
    .filter((bundle) => bundle.status === 'past_due' || bundle.cancel_at_period_end)
    .reduce((sum, bundle) => sum + annualizedUsd(bundle), 0);

  const activationStages = useMemo(() => {
    const total = bundles.length;
    const entitled = entitledBundles.length;
    const onboarded = entitledBundles.filter((bundle) => (
      !includesSiteComponent(bundle) || Number(bundle.website_count) > 0
    )).length;
    const live = entitledBundles.filter((bundle) => (
      includesSiteComponent(bundle) && Number(bundle.deployed_website_count) > 0
    )).length;
    return [
      { label: 'Bundle opened', count: total, detail: 'Sales-created accounts' },
      { label: 'Entitled', count: entitled, detail: 'Active or grace period' },
      { label: 'Onboarded', count: onboarded, detail: 'Site built, or no site in the plan' },
      { label: 'Site live', count: live, detail: 'First customer outcome' },
    ];
  }, [bundles, entitledBundles]);

  const productAdoption = useMemo(() => PRODUCT_META.map((product) => {
    const count = entitledBundles.filter((bundle) => {
      const components = parseComponents(bundle.components);
      if (product.key === 'omni') {
        return components.includes('omni') || components.includes('whatsapp_bot');
      }
      return components.includes(product.key);
    }).length;
    return {
      ...product,
      count,
      percentage: entitledBundles.length > 0 ? Math.round((count / entitledBundles.length) * 100) : 0,
    };
  }), [entitledBundles]);

  const accountRows = useMemo(() => [...bundles]
    .sort((a, b) => {
      const aAttention = getAttention(a)?.priority ?? 99;
      const bAttention = getAttention(b)?.priority ?? 99;
      if (aAttention !== bAttention) return aAttention - bAttention;
      return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    })
    .slice(0, 10), [bundles]);

  useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (previousBundlesRef.current !== null && activeBundles > previousBundlesRef.current) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
    previousBundlesRef.current = activeBundles;
    return undefined;
  }, [activeBundles]);

  useEffect(() => {
    if (autoRefresh === 'off') return undefined;
    const intervals = { '1min': 60000, '3min': 180000, '5min': 300000 };
    const interval = setInterval(() => setRefreshKey((value) => value + 1), intervals[autoRefresh]);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loading = analyticsLoading || bundlesLoading;

  return (
    <AdminLayout>
      <div className="pb-10">
        {showConfetti && (
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            numberOfPieces={350}
            recycle={false}
            colors={['#0f172a', '#0284c7', '#10b981', '#f59e0b']}
          />
        )}

        <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Zappy command center</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">Business Pulse</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              See which accounts are activating, which need help, and where recurring revenue is exposed.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/accounts"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            >
              <BuildingOfficeIcon className="h-4 w-4" />
              Accounts
            </Link>
            <Link
              to="/bundles"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <SparklesIcon className="h-4 w-4" />
              Manage bundles
            </Link>
            <select
              value={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.value)}
              className="input-field w-36 text-sm"
              aria-label="Automatic refresh interval"
            >
              <option value="off">Refresh: Off</option>
              <option value="1min">Every minute</option>
              <option value="3min">Every 3 min</option>
              <option value="5min">Every 5 min</option>
            </select>
            <button
              type="button"
              onClick={() => setRefreshKey((value) => value + 1)}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              title="Refresh dashboard"
              aria-label="Refresh dashboard"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </header>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Recurring revenue summary">
          <MetricCard
            label="Annual recurring revenue"
            value={formatMoney(annualRecurringRevenue, { compact: true })}
            detail="All recurring plans and add-ons"
            icon={ArrowTrendingUpIcon}
            tone="slate"
            loading={analyticsLoading}
          />
          <MetricCard
            label="Monthly recurring revenue"
            value={formatMoney(annualRecurringRevenue / 12, { compact: true })}
            detail="Normalized from current ARR"
            icon={BanknotesIcon}
            tone="blue"
            loading={analyticsLoading}
          />
          <MetricCard
            label="Active bundles"
            value={activeBundles.toLocaleString()}
            detail={`${analytics?.data?.subscriptions?.activeSitePlans || 0} legacy site plans remain`}
            icon={CreditCardIcon}
            tone="green"
            loading={loading}
          />
          <MetricCard
            label="Revenue at risk"
            value={formatMoney(revenueAtRisk, { compact: true })}
            detail="Past due or cancelling ARR"
            icon={ExclamationTriangleIcon}
            tone="rose"
            loading={bundlesLoading}
          />
        </section>

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-950 shadow-sm">
          <div className="flex flex-col gap-2 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">From sale to value</p>
              <h2 className="mt-1 text-xl font-semibold">Activation journey</h2>
            </div>
            <Link to="/bundles" className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-800">
              View portfolio
              <ChevronRightIcon className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-px bg-slate-100 md:grid-cols-4">
            {activationStages.map((stage, index) => {
              const previous = index > 0 ? activationStages[index - 1].count : stage.count;
              const conversion = index === 0
                ? 100
                : previous > 0 ? Math.round((stage.count / previous) * 100) : 0;
              return (
                <div key={stage.label} className="relative bg-white px-6 py-6">
                  <div className="flex items-center justify-between">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-sky-50 text-xs font-semibold text-sky-700">
                      {index + 1}
                    </span>
                    {index > 0 && (
                      <span className="text-xs font-medium text-slate-500">{conversion}% from prior</span>
                    )}
                  </div>
                  {bundlesLoading ? (
                    <div className="mt-5 h-9 w-16 animate-pulse rounded bg-slate-100" />
                  ) : (
                    <p className="mt-4 text-3xl font-semibold">{stage.count.toLocaleString()}</p>
                  )}
                  <p className="mt-1 font-medium text-slate-900">{stage.label}</p>
                  <p className="mt-1 text-xs text-slate-500">{stage.detail}</p>
                  <div className="mt-5 h-1 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-sky-500"
                      style={{ width: `${activationStages[0].count > 0 ? (stage.count / activationStages[0].count) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {bundlesError && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Engagement Bundle data is unavailable. Revenue totals still reflect the analytics overview.
          </div>
        )}

        <div className="mt-6 grid gap-6 xl:grid-cols-3">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm xl:col-span-2">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h2 className="font-semibold text-slate-950">Needs attention</h2>
                <p className="mt-0.5 text-sm text-slate-500">Prioritized accounts where one action can protect or unlock revenue.</p>
              </div>
              <span className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                {attentionItems.length} shown
              </span>
            </div>
            {bundlesLoading ? (
              <div className="space-y-3 p-5">
                {[1, 2, 3].map((item) => <div key={item} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}
              </div>
            ) : attentionItems.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {attentionItems.map(({ bundle, health, title, detail, action }) => (
                  <Link
                    key={bundle.account_id}
                    to={`/bundles/${bundle.account_id}`}
                    className="group flex items-center gap-4 px-5 py-4 hover:bg-slate-50"
                  >
                    <div className={`rounded-xl p-2.5 ${health.key === 'risk' ? 'bg-rose-50 text-rose-600' : health.key === 'onboarding' ? 'bg-sky-50 text-sky-600' : 'bg-amber-50 text-amber-600'}`}>
                      {health.key === 'risk'
                        ? <ExclamationTriangleIcon className="h-5 w-5" />
                        : health.key === 'onboarding'
                          ? <BoltIcon className="h-5 w-5" />
                          : <ClockIcon className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2">
                        <p className="truncate font-medium text-slate-900">{bundle.account_name || bundle.account_id}</p>
                        <span className="text-xs capitalize text-slate-400">{bundle.tier}</span>
                      </div>
                      <p className="mt-0.5 text-sm text-slate-700">{title}</p>
                      <p className="text-xs text-slate-500">{detail}</p>
                    </div>
                    <span className="hidden text-sm font-medium text-sky-700 group-hover:text-sky-800 sm:inline">{action}</span>
                    <ChevronRightIcon className="h-4 w-4 text-slate-300 group-hover:text-sky-600" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center px-5 py-12 text-center">
                <CheckCircleIcon className="h-9 w-9 text-emerald-500" />
                <p className="mt-3 font-medium text-slate-900">No accounts need immediate action</p>
                <p className="mt-1 text-sm text-slate-500">Every active bundle is paid, live, and has sufficient credits.</p>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div>
              <h2 className="font-semibold text-slate-950">Product adoption</h2>
              <p className="mt-0.5 text-sm text-slate-500">Enabled across entitled Engagement Bundles.</p>
            </div>
            <div className="mt-5 space-y-5">
              {productAdoption.map((product) => {
                const Icon = product.icon;
                return (
                  <div key={product.key}>
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-slate-100 p-2 text-slate-600">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-medium text-slate-800">{product.label}</p>
                          <p className="text-sm font-semibold text-slate-950">{product.percentage}%</p>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                          <div className={`h-full rounded-full ${product.color}`} style={{ width: `${product.percentage}%` }} />
                        </div>
                      </div>
                    </div>
                    <p className="mt-1 text-right text-xs text-slate-400">{product.count} of {entitledBundles.length} accounts</p>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold text-slate-950">Account health</h2>
              <p className="mt-0.5 text-sm text-slate-500">The highest-priority bundle accounts, with setup and credit context.</p>
            </div>
            <Link to="/bundles" className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-800">
              All bundle accounts
              <ChevronRightIcon className="h-4 w-4" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/70">
                <tr>
                  {['Account', 'Bundle', 'Activation', 'Credits remaining', 'Recurring value', 'Health', ''].map((heading) => (
                    <th
                      key={heading || 'open'}
                      className="whitespace-nowrap px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {accountRows.map((bundle) => {
                  const health = getHealth(bundle);
                  const credits = creditState(bundle);
                  const websites = Number(bundle.website_count) || 0;
                  const live = Number(bundle.deployed_website_count) || 0;
                  return (
                    <tr key={bundle.account_id} className="hover:bg-slate-50/70">
                      <td className="whitespace-nowrap px-5 py-4">
                        <Link to={`/bundles/${bundle.account_id}`} className="font-medium text-slate-900 hover:text-sky-700">
                          {bundle.account_name || bundle.account_id}
                        </Link>
                        <p className="mt-0.5 max-w-[180px] truncate font-mono text-[10px] text-slate-400">{bundle.account_id}</p>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm capitalize text-slate-700">
                        {bundle.tier}
                        <p className="mt-0.5 text-xs capitalize text-slate-400">{String(bundle.status || '').replace('_', ' ')}</p>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <p className="text-sm text-slate-700">{live > 0 ? 'Live' : websites > 0 ? 'Draft' : 'No site'}</p>
                        <p className="mt-0.5 text-xs text-slate-400">{websites} site{websites === 1 ? '' : 's'} · {live} live</p>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <p className="text-sm font-medium text-slate-700">
                          {credits.remainingPct === null ? '—' : `${credits.remainingPct}%`}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-400">{credits.available.toLocaleString()} available</p>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <p className="text-sm font-medium text-slate-700">
                          {bundle.currency === 'ILS' ? '₪' : '$'}{Number(bundle.amount || 0).toLocaleString()}
                        </p>
                        <p className="mt-0.5 text-xs capitalize text-slate-400">{bundle.billing_period || '—'}</p>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ring-1 ring-inset ${HEALTH_STYLES[health.key]}`}>
                          {health.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Link to={`/bundles/${bundle.account_id}`} aria-label={`Open ${bundle.account_name || 'bundle account'}`}>
                          <ChevronRightIcon className="h-5 w-5 text-slate-300 hover:text-sky-600" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
                {!bundlesLoading && accountRows.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-5 py-10 text-center text-sm text-slate-500">
                      No Engagement Bundle accounts yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <GeneratingWebsitesWidget refreshKey={refreshKey} />
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
